import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { toMe, toMeFromPortal } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { ClientMeRead, MeRead } from '@/shared/api/contracts'
import { isApiError } from '@/shared/api/errors'
import { qk } from '@/shared/api/query-keys'
import type { Me } from '@/shared/types/domain'

import { useAuth } from './auth-context'

/**
 * `GET /auth/me`, con caída a `GET /portal/me`.
 *
 * La ruta es `auth/me`, no `me`: `GET /api/v1/me` no existe en el backend y
 * todo el arranque de sesión contra el servicio real daba 404.
 *
 * **El rol sale de aca, nunca de decodificar el JWT.** El token solo prueba
 * QUIEN sos; que podes hacer lo decide el backend, que es el unico que ve la
 * fila de `users` y sus membresias.
 *
 * ¿Por qué la caída? `auth/me` vive en el router interno, que lleva
 * `Depends(deny_clients)`: a un contacto de cliente le contesta **403**, a
 * propósito — su superficie es `/api/v1/portal`. Pero la sesión se derivaba
 * SOLO de `auth/me`, así que ese 403 dejaba a `useSession()` en `anonymous` y
 * `<RequireAuth>` mandaba al contacto a `/login`: **el portal entero era
 * inalcanzable para su única audiencia**. Los unitarios no lo veían porque
 * mockeaban `/me` devolviendo `role: 'client'`.
 *
 * La caída es sólo para el 403 y sólo hacia el portal. Un 401 no se reintenta
 * (ya lo resolvió el interceptor) y un cliente que tampoco tenga `/portal/me`
 * queda anónimo, que es lo correcto.
 */
async function fetchMe(): Promise<Me> {
  try {
    return toMe(await api.get<MeRead>('auth/me'))
  } catch (error) {
    if (isApiError(error) && error.isForbidden) {
      return toMeFromPortal(await api.get<ClientMeRead>('portal/me'))
    }
    throw error
  }
}

export function useMe(): UseQueryResult<Me, unknown> {
  const { status } = useAuth()

  return useQuery({
    queryKey: qk.auth.me(),
    queryFn: fetchMe,
    enabled: status === 'authenticated',
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      // 401 y 403 no se reintentan: ya los resolvio el interceptor.
      if (isApiError(error) && (error.isUnauthorized || error.isForbidden)) return false
      return failureCount < 2
    },
  })
}
